import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MentionAutocomplete, { buildRefLabel } from './MentionAutocomplete';

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Document types image RAG indexes (the picker shows only these).
const ATTACHABLE_DOC_EXT = /\.(pdf|docx?|pptx?)$/i;


const PLACEMENT = {
  body: { title: 'Add figure', hint: 'Placed in the text where you pasted it', icon: 'bi-text-paragraph' },
  cell: { title: 'Add image to table cell', hint: 'Placed in the table cell you pasted into', icon: 'bi-table' },
};

/**
 * Popup shown when an image is pasted or dropped into an agent response.
 * The image is uploaded only on Insert, so cancelling leaves nothing behind.
 *
 * Built from existing styles: an ev-card-2 box with the genbi-fig-* popup shell,
 * the Extend/Amend option cards (ev-card-2 +
 * active-content-style), the ThemedSelect search + options (themed-select__*),
 * the in-place image preview and the shared form fields. It is a div inside the
 * in-place modal overlay rather than a <dialog>, so the @ picker (portalled to
 * <body>) is not hidden behind the browser's top layer.
 *
 * props:
 *   file       - the pasted File
 *   placement  - 'body' (figure at the cursor) or 'cell' (in-place figure in a table cell)
 *   docs       - the agent's documents [{ id, name, groupName }]: "Attach to document" and @ citations
 *   refs, onNeedReferences, onCreateReference, refsLoading - @ reference citations, as in
 *                the other figure edit popups
 *   onCancel() - close without inserting
 *   onConfirm({ subtitle, footer, attachDocId }) - must return a Promise; it uploads and inserts
 */
const ImagePasteModal = ({
  file, placement = 'body', docs = [], onCancel, onConfirm,
  refs = [], onNeedReferences, onCreateReference, refsLoading = false,
}) => {
  const [subtitle, setSubtitle] = useState('');
  const [footer, setFooter] = useState('');
  const [attachMode, setAttachMode] = useState('response'); // 'response' | 'document'
  const [attachDocId, setAttachDocId] = useState(null);
  const [docQuery, setDocQuery] = useState('');
  // Doc picker grouping, as in Configure Agent: by data folder or by meta tag
  const [groupBy, setGroupBy] = useState('folders');
  // Groups the user folded in the doc picker (all start open)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dims, setDims] = useState(null);
  // Chip label per citation token, as the @ picker built it ({ '[ar_id_7]': 'Smith et al., 2021' })
  const [citeLabels, setCiteLabels] = useState({});
  const subtitleRef = useRef(null);
  const footerRef = useRef(null);
  const place = PLACEMENT[placement] || PLACEMENT.body;

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const fileKind = (file?.type || '').replace('image/', '').toUpperCase() || 'IMAGE';
  const metaLine = [fileKind, dims && `${dims.w} × ${dims.h}`, formatBytes(file?.size)].filter(Boolean).join(' · ');

  // Image RAG runs only on PDF, Word and PowerPoint documents; spreadsheets and CSVs
  // are read by the dataframe tool and never searched for images. All docs stay
  // available for @ citations.
  const attachableDocs = useMemo(
    () => docs.filter(d => ATTACHABLE_DOC_EXT.test(d.name || '')),
    [docs]
  );

  const filteredDocs = useMemo(() => {
    const q = docQuery.trim().toLowerCase();
    return attachableDocs.filter(d => !q || `${d.name || ''} ${d.groupName || ''}`.toLowerCase().includes(q));
  }, [attachableDocs, docQuery]);

  // Same shape as the Configure Agent doc picker: a group (data folder or meta tag),
  // then its documents. Under tags a document shows once per tag of its folder.
  const docGroups = useMemo(() => {
    const byName = new Map();
    const add = (key, d) => {
      if (!byName.has(key)) byName.set(key, []);
      byName.get(key).push(d);
    };
    filteredDocs.forEach(d => {
      if (groupBy === 'tags') {
        if (d.tags?.length) d.tags.forEach(t => add(t, d));
        else add('No tags', d);
      } else {
        add(d.groupName || 'Other documents', d);
      }
    });
    return [...byName.entries()].map(([name, items]) => ({ key: `${groupBy}:${name}`, name, items }));
  }, [filteredDocs, groupBy]);
  const allCollapsed = docGroups.length > 0 && docGroups.every(g => collapsedGroups.has(g.key));
  const toggleAllGroups = () => setCollapsedGroups(allCollapsed ? new Set() : new Set(docGroups.map(g => g.key)));
  const toggleGroup = (name) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const selectedDoc = attachableDocs.find(d => d.id === attachDocId) || null;
  const canInsert = !busy && (attachMode === 'response' || !!attachDocId);

  const handleInsert = useCallback(async () => {
    if (!canInsert) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm({
        subtitle: subtitle.trim(),
        footer: footer.trim(),
        attachDocId: attachMode === 'document' ? attachDocId : null,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Image upload failed');
      setBusy(false);
    }
  }, [canInsert, onConfirm, subtitle, footer, attachMode, attachDocId]);

  const close = useCallback(() => { if (!busy) onCancel(); }, [busy, onCancel]);

  // Esc closes, Ctrl/Cmd+Enter inserts.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleInsert(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [close, handleInsert]);

  // Caption preview: show [doc_id_N] / [ar_id_N] tokens as the chip they become.
  const withCiteChips = (text) => text.split(/(\[(?:doc_id|ar_id)_\d+\])/g).map((part, i) => {
    const m = part.match(/^\[(doc_id|ar_id)_(\d+)\]$/);
    if (!m) return part;
    const id = Number(m[2]);
    const isDoc = m[1] === 'doc_id';
    const ref = isDoc ? null : refs.find(r => Number(r.ar_id) === id);
    const name = citeLabels[part]
      || (isDoc ? docs.find(d => Number(d.id) === id)?.name : (ref && buildRefLabel(ref)))
      || part;
    const short = name.length > 20 ? `${name.slice(0, 19)}…` : name;
    return (
      <span key={i} className={`${isDoc ? 'ev-message-card6' : 'ev-message-card7'} m-0 mx-1 ev-btn-xsmall ev-bold-reg`} title={name}>
        {short}
      </span>
    );
  });

  // Type @ in either field to cite a document or reference (same picker as the editor).
  const mention = (ref, setValue) => (
    <MentionAutocomplete
      textareaRef={ref}
      docs={docs}
      refs={refs}
      onNeedReferences={onNeedReferences}
      onCreateReference={onCreateReference}
      refsLoading={refsLoading}
      onInsert={(newValue, newCaret) => {
        // Subtitle/note are plain text (as in ImageComponent): keep the citation as its
        // [doc_id_N]/[ar_id_N] token, which the response view and report turn into a chip.
        const chipRe = /<span[^>]*data-ref="([^"]+)"[^>]*>([^<]*)<\/span>/g;
        const labels = {};
        newValue.replace(chipRe, (_, token, label) => { labels[token] = label; return ''; });
        if (Object.keys(labels).length) setCiteLabels(prev => ({ ...prev, ...labels }));
        const toToken = (s) => s.replace(chipRe, '$1');
        const v = toToken(newValue);
        const caret = toToken(newValue.slice(0, newCaret)).length;
        setValue(v);
        window.requestAnimationFrame(() => {
          const ta = ref.current;
          if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
        });
      }}
    />
  );

  const optionCard = (mode, icon, label, hint) => {
    const on = attachMode === mode;
    return (
      <div
        role="radio"
        aria-checked={on}
        tabIndex={busy ? -1 : 0}
        className={`ev-card-2 genbi-where-card flex-fill d-flex align-items-start gap-2 p-2 cursor-pointer ${on ? 'active-content-style' : ''}`}
        onClick={() => !busy && setAttachMode(mode)}
        onKeyDown={e => { if (!busy && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setAttachMode(mode); } }}
      >
        <i className={`bi ${icon} ev-reg-p-blue`} />
        <div className="d-flex flex-column">
          <span className="ev-small-p-blue ev-bold-med">{label}</span>
          <span className="ev-xsmall-p-dark opacity-75">{hint}</span>
        </div>
        {on && <i className="bi bi-check-circle-fill ev-reg-p-blue ms-auto" />}
      </div>
    );
  };

  return createPortal((
    <div className="genbi-inplace-modal-overlay" onMouseDown={close}>
      <div
        open
        className="ev-card-2 genbi-fig-modal genbi-add-figure-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="genbi-add-figure-title"
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Same header as the crop popup: title, then one muted line (where it goes, file) */}
        <header className="genbi-fig-modal-header genbi-crop-header">
          <div className="genbi-fig-heading">
            <div className="genbi-fig-title-row">
              <h2 id="genbi-add-figure-title" className="genbi-fig-modal-title">{place.title}</h2>
            </div>
            <p className="genbi-crop-subtitle">
              <i className={`bi ${place.icon} me-1`} />{place.hint}
              {metaLine && <span className="genbi-figure-meta">{metaLine}</span>}
            </p>
          </div>
          <button type="button" className="genbi-fig-close" onClick={close} disabled={busy} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div className="genbi-fig-modal-body scrollable-element">
          <div className="row g-3">
            {/* Preview + how the caption will read */}
            <div className="col-md-5 d-flex flex-column gap-3">
              <div>
                <p className="genbi-fig-label">Image</p>
                <div className="genbi-inplace-img-preview">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt=""
                      className="genbi-inplace-img-preview-img"
                      onLoad={e => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                    />
                  )}
                </div>
              </div>
              <div className="genbi-fig-selection">
                <p className="genbi-fig-label">Caption in the report</p>
                <blockquote>
                  <b>Figure.</b>{' '}
                  {subtitle.trim()
                    ? withCiteChips(subtitle.trim())
                    : <span className="opacity-50">Your subtitle</span>}
                  {footer.trim() && (
                    <div className="mt-1 ev-fs-12"><b>Note</b> {withCiteChips(footer.trim())}</div>
                  )}
                </blockquote>
              </div>
            </div>

            {/* Fields */}
            <div className="col-md-7 d-flex flex-column gap-3">
              <div>
                <p className="genbi-fig-label">Subtitle</p>
                <textarea
                  ref={subtitleRef}
                  className="formField2 ev-fs-12 w-100"
                  value={subtitle}
                  onChange={e => setSubtitle(e.target.value)}
                  rows={2}
                  placeholder="e.g. Cost per responder by country — type @ to cite"
                  disabled={busy}
                  autoFocus
                />
                {mention(subtitleRef, setSubtitle)}
              </div>

              <div>
                <p className="genbi-fig-label">Note <span className="text-lowercase opacity-75">(optional)</span></p>
                <textarea
                  ref={footerRef}
                  className="formField2 ev-fs-12 w-100"
                  value={footer}
                  onChange={e => setFooter(e.target.value)}
                  rows={2}
                  placeholder="Source, abbreviations, footnotes…"
                  disabled={busy}
                />
                {mention(footerRef, setFooter)}
              </div>

              {attachableDocs.length > 0 && (
                <div>
                  <p className="genbi-fig-label">Where it lives</p>
                  <div className="d-flex gap-2" role="radiogroup">
                    {optionCard('response', 'bi-chat-square-text', 'Response only', 'Shown here and in the report')}
                    {optionCard('document', 'bi-file-earmark-plus', 'Also add to a document', 'Agents can find it when they search it')}
                  </div>

                  {attachMode === 'document' && (
                    <div className="mt-2">
                      {/* The app's standard search bar (Transfer Ownership, admin lists, …) */}
                      <div className="position-relative mb-2">
                        <input
                          type="text"
                          className="form-control search-input ms-0"
                          value={docQuery}
                          onChange={e => setDocQuery(e.target.value)}
                          placeholder={`Search ${attachableDocs.length} documents`}
                          disabled={busy}
                        />
                        <button type="button" className="ev-btn-lightblue-fill-round search-bar-button" tabIndex={-1} aria-hidden="true">
                          <i className="bi bi-search"></i>
                        </button>
                      </div>
                      {/* Group by folder / tag, and fold all: the Configure Agent controls, small */}
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <div className="ev-view-toggle-shell genbi-attach-doc-toggle" role="radiogroup" aria-label="Group documents by">
                          <button type="button" role="radio" aria-checked={groupBy === 'folders'} className={`ev-view-toggle-btn ${groupBy === 'folders' ? 'active' : ''}`} onClick={() => setGroupBy('folders')}>
                            Data Folders
                          </button>
                          <button type="button" role="radio" aria-checked={groupBy === 'tags'} className={`ev-view-toggle-btn ${groupBy === 'tags' ? 'active' : ''}`} onClick={() => setGroupBy('tags')}>
                            Meta Tags
                          </button>
                        </div>
                        <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall" onClick={toggleAllGroups} disabled={!docGroups.length}>
                          <i className={`bi ${allCollapsed ? 'bi-arrows-expand' : 'bi-arrows-collapse'} me-1`} />{allCollapsed ? 'Expand All' : 'Collapse All'}
                        </button>
                      </div>
                      {/* Group > documents, as in Configure Agent; one document is picked */}
                      <div className="scrollable-element genbi-attach-doc-tree" role="radiogroup" aria-label="Document">
                        {docGroups.length === 0 && (
                          <p className="ev-small-p-blue text-center my-2">No document matches “{docQuery}”</p>
                        )}
                        {docGroups.map(g => {
                          const open = !collapsedGroups.has(g.key) || !!docQuery.trim();
                          return (
                            <div key={g.key} className="ev-card-3 mb-2">
                              <div className="d-flex align-items-center">
                                <button type="button" className="btn btn-sm p-0 me-2" onClick={() => toggleGroup(g.key)} aria-expanded={open} aria-label={open ? 'Collapse folder' : 'Expand folder'}>
                                  <i className={`ev-reg-p-dark bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
                                </button>
                                <span className="ev-small-p-blue ev-bold-reg text-truncate" title={g.name}>
                                  <i className={`bi ${groupBy === 'tags' ? 'bi-tag' : 'bi-folder2'} me-1`} />{g.name}
                                </span>
                                <span className="badge bg-warning ms-2 ev-btn-small">{g.items.length} docs</span>
                              </div>
                              {open && (
                                <div className="mt-2 ms-4">
                                  {g.items.map(d => {
                                    const sel = d.id === attachDocId;
                                    return (
                                      <div key={`${g.key}:${d.id}`}>
                                        <label className={`ev-message-card1 d-inline-flex align-items-center pe-2 mb-1 genbi-attach-doc-pill ${sel ? 'is-selected' : ''}`} title={d.name}>
                                          <input
                                            className="form-check-input me-2 mt-0"
                                            type="radio"
                                            name="genbi-attach-doc"
                                            checked={sel}
                                            onChange={() => setAttachDocId(d.id)}
                                            disabled={busy}
                                          />
                                          <span className="ev-xsmall-p-blue text-truncate">{d.name}</span>
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="ev-alert-card-warning ev-fs-11 mb-0" role="alert">
                  <i className="bi bi-exclamation-triangle me-1" />{error}
                </div>
              )}
            </div>
          </div>
        </div>

        <footer className="genbi-fig-modal-footer">
          <span className="genbi-figure-footer-note">
            {attachMode === 'document' && selectedDoc
              ? <><i className="bi bi-paperclip" /><span className="text-truncate" title={selectedDoc.name}>Also indexed with <b>{selectedDoc.name}</b></span></>
              : <><i className="bi bi-keyboard" />Ctrl+Enter to insert · Esc to cancel</>}
          </span>
          <div className="d-flex align-items-center gap-2 flex-shrink-0 text-nowrap">
            {/* Same pill pair as the crop popup */}
            <button type="button" className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small px-3" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="ev-btn-blue-fill-round ev-bold-reg ev-btn-small px-4" onClick={handleInsert} disabled={!canInsert}>
              {busy
                ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />Uploading…</>
                : 'Insert figure'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  ), document.body);
};

export default ImagePasteModal;
